import * as React from "react"
import { Link, createFileRoute, useRouter } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import { Play, Trash2 } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { MemberShell } from "@/components/member-shell"
import { deleteSavedSearchFn, getSavedSearchesFn } from "@/lib/app.functions"
import { requireMemberRoute } from "@/lib/route-guards"
import type { SavedSearch } from "@/lib/types"

export const Route = createFileRoute("/saved-searches")({
  loader: async () => {
    await requireMemberRoute("/saved-searches")
    return getSavedSearchesFn()
  },
  head: () => ({
    meta: [{ title: "Saved searches · Harbor & Pine Realty" }],
  }),
  component: SavedSearchesPage,
})

function SavedSearchesPage() {
  const { user, searches } = Route.useLoaderData()
  return (
    <MemberShell
      user={user}
      eyebrow="Saved filters · quick access"
      title="Saved searches"
      intro="Return to a prepared set of criteria without reconstructing the search field by field."
      action={
        <Button
          className="field-button"
          nativeButton={false}
          render={<Link to="/search" />}
        >
          Create from search
        </Button>
      }
    >
      {searches.length ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {searches.map((search, index) => (
            <SavedSearchCard
              key={search.id}
              search={search}
              index={index + 1}
            />
          ))}
        </div>
      ) : (
        <p className="border border-dashed bg-white p-7 text-sm text-muted-foreground">
          No saved searches yet. Run a property search, then choose “Save
          search.”
        </p>
      )}
    </MemberShell>
  )
}

function SavedSearchCard({
  search,
  index,
}: {
  search: SavedSearch
  index: number
}) {
  const remove = useServerFn(deleteSavedSearchFn)
  const router = useRouter()
  const [pending, setPending] = React.useState(false)
  const facts = Object.entries(search.filters).filter(([, value]) =>
    Boolean(value)
  )

  const handleDelete = async () => {
    setPending(true)
    try {
      await remove({ data: { id: search.id } })
      await router.invalidate()
      toast.success("Saved search removed.")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to delete.")
    } finally {
      setPending(false)
    }
  }

  return (
    <article className="field-panel p-5">
      <div className="flex items-start justify-between gap-4">
        <span className="field-marker">{index}</span>
        <span className="field-label">
          {new Intl.DateTimeFormat("en-US", { dateStyle: "medium" }).format(
            new Date(search.createdAt)
          )}
        </span>
      </div>
      <h2 className="mt-5 font-heading text-3xl uppercase">{search.name}</h2>
      <dl className="mt-4 flex min-h-14 flex-wrap gap-2">
        {facts.map(([key, value]) => (
          <div
            key={key}
            className="border border-border bg-muted px-2 py-1 text-xs"
          >
            <dt className="inline text-muted-foreground">{key}: </dt>
            <dd className="inline font-semibold">{String(value)}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-6 flex gap-2 border-t pt-4">
        <Button
          className="field-button flex-1"
          nativeButton={false}
          render={<Link to="/search" search={search.filters} />}
        >
          <Play />
          Run search
        </Button>
        <Button
          variant="destructive"
          className="field-button"
          onClick={handleDelete}
          disabled={pending}
        >
          <Trash2 />
          Remove
        </Button>
      </div>
    </article>
  )
}
